<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';
import { useMetaCampaignsCampaignBreakdownStore } from 'dashboard/stores/metaCampaignsCampaignBreakdown';
import { useMetaCampaignsFormatters } from '../composables/useMetaCampaignsFormatters';
import AdCreativeThumbnail from './AdCreativeThumbnail.vue';

const props = defineProps({
  campaignId: {
    type: [Number, String],
    required: true,
  },
  since: {
    type: Number,
    required: true,
  },
  until: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const { formatFullNumber } = useNumberFormatter();
const { brl, roasClass } = useMetaCampaignsFormatters();
const breakdownStore = useMetaCampaignsCampaignBreakdownStore();

const result = computed(() => breakdownStore.ads);
const rows = computed(() => result.value?.rows || []);
const isLoading = computed(() => breakdownStore.uiFlags.fetchingAds);

const coverageNote = computed(() => {
  const coverage = result.value?.attribution_coverage;
  if (!coverage?.campaign_leads) return '';
  if (coverage.attributed_leads >= coverage.campaign_leads) return '';

  return t('META_CAMPAIGNS.CAMPAIGN_DETAIL.BREAKDOWN_COVERAGE', {
    attributed: coverage.attributed_leads,
    total: coverage.campaign_leads,
  });
});

const fetchAds = () => {
  breakdownStore.fetchAds(props.campaignId, {
    since: props.since,
    until: props.until,
  });
};

watch(() => [props.campaignId, props.since, props.until], fetchAds, {
  immediate: true,
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="rounded-xl border border-n-strong bg-n-alpha-3 overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak text-xs text-n-slate-10">
            <th class="text-left font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.AD_TABLE.NAME') }}
            </th>
            <th class="text-left font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.AD_TABLE.AD_SET') }}
            </th>
            <th class="text-left font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.AD_TABLE.STATUS') }}
            </th>
            <th class="text-right font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.AD_TABLE.SPEND') }}
            </th>
            <th class="text-right font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.AD_TABLE.IMPRESSIONS') }}
            </th>
            <th class="text-right font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.AD_TABLE.CLICKS') }}
            </th>
            <th class="text-right font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.AD_TABLE.LEADS') }}
            </th>
            <th class="text-right font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.AD_TABLE.REVENUE') }}
            </th>
            <th class="text-right font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.AD_TABLE.ROAS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            class="border-b border-n-weak last:border-0"
          >
            <td class="px-4 py-2.5">
              <div class="flex items-center gap-2.5">
                <AdCreativeThumbnail
                  :thumbnail-url="row.thumbnail_url"
                  :creative-type="row.creative_type"
                  :alt="row.name"
                />
                <span class="text-n-slate-12 font-medium">{{ row.name }}</span>
              </div>
            </td>
            <td class="px-4 py-2.5 text-n-slate-11">
              {{ row.ad_set_name || '—' }}
            </td>
            <td class="px-4 py-2.5">
              <span
                class="text-xs px-2 py-0.5 rounded-full bg-n-slate-3 text-n-slate-11"
              >
                {{ row.status }}
              </span>
            </td>
            <td class="px-4 py-2.5 text-right text-n-slate-12">
              {{ brl(row.spend) }}
            </td>
            <td class="px-4 py-2.5 text-right text-n-slate-11">
              {{ formatFullNumber(row.impressions) }}
            </td>
            <td class="px-4 py-2.5 text-right text-n-slate-11">
              {{ formatFullNumber(row.clicks) }}
            </td>
            <td class="px-4 py-2.5 text-right text-n-slate-11">
              {{ formatFullNumber(row.leads) }}
            </td>
            <td class="px-4 py-2.5 text-right text-n-slate-12">
              {{ brl(row.revenue) }}
            </td>
            <td
              class="px-4 py-2.5 text-right font-medium"
              :class="roasClass(row.roas)"
            >
              {{ row.roas ? `${formatFullNumber(row.roas)}x` : '—' }}
            </td>
          </tr>
          <tr v-if="isLoading && !rows.length">
            <td colspan="9" class="px-4 py-6 text-center">
              <span
                class="i-lucide-loader-circle animate-spin text-lg text-n-slate-9"
              />
            </td>
          </tr>
          <tr v-else-if="!rows.length">
            <td
              colspan="9"
              class="px-4 py-6 text-center text-sm text-n-slate-9"
            >
              {{ t('META_CAMPAIGNS.AD_TABLE.NO_DATA') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p v-if="coverageNote" class="text-xs text-n-slate-10">
      {{ coverageNote }}
    </p>
  </div>
</template>
